import { QRCodeSVG } from "qrcode.react";

export const RemnantLabel = ({ r, forwardRef }) => {
  const url = `${window.location.origin}/inventory/remnants/${r.id}`;
  const fullSqft = Math.round(((r.length_in * r.width_in) / 144) * 10) / 10;
  const poly = r.polygon || [];
  const round1 = (n) => Math.round(n * 10) / 10;
  const usableL =
    poly.length > 2
      ? round1(
          (Math.max(...poly.map((p) => p.x)) -
            Math.min(...poly.map((p) => p.x))) *
            r.length_in,
        )
      : r.length_in;
  const usableW =
    poly.length > 2
      ? round1(
          (Math.max(...poly.map((p) => p.y)) -
            Math.min(...poly.map((p) => p.y))) *
            r.width_in,
        )
      : r.width_in;

  // shape drawn from the polygon bounding box so it fills the tag
  const SW = 320;
  const SH = 150;
  const PAD = 8;
  const minX = poly.length > 2 ? Math.min(...poly.map((p) => p.x)) : 0;
  const maxX = poly.length > 2 ? Math.max(...poly.map((p) => p.x)) : 1;
  const minY = poly.length > 2 ? Math.min(...poly.map((p) => p.y)) : 0;
  const maxY = poly.length > 2 ? Math.max(...poly.map((p) => p.y)) : 1;
  const spanX = maxX - minX || 1;
  const spanY = maxY - minY || 1;
  const boxW = SW - PAD * 2 - 26;
  const boxH = SH - PAD * 2 - 26;
  const shapePts = poly
    .map(
      (p) =>
        `${PAD + ((p.x - minX) / spanX) * boxW},${PAD + ((p.y - minY) / spanY) * boxH}`,
    )
    .join(" ");

  const sides = poly.map((p, i) => {
    const q = poly[(i + 1) % poly.length];
    const dx = (q.x - p.x) * r.length_in;
    const dy = (q.y - p.y) * r.width_in;
    return {
      len: round1(Math.sqrt(dx * dx + dy * dy)),
      mx: PAD + (((p.x + q.x) / 2 - minX) / spanX) * boxW,
      my: PAD + (((p.y + q.y) / 2 - minY) / spanY) * boxH,
    };
  });

  return (
    <div
      className="label-4x6 print-label"
      ref={forwardRef}
      data-testid="remnant-label-sheet"
    >
      <div className="flex items-start justify-between border-b-2 border-black pb-1">
        <div>
          <div
            style={{
              fontSize: "9pt",
              fontWeight: 700,
              letterSpacing: "0.18em",
            }}
          >
            REMNANT
          </div>
          <div
            className="font-mono-data"
            style={{ fontSize: "18pt", fontWeight: 700 }}
          >
            {r.code}
          </div>
        </div>
        <div style={{ textAlign: "right", fontSize: "9pt", fontWeight: 700 }}>
          <div style={{ border: "2px solid #000", padding: "2px 6px" }}>
            {(r.status || "").toUpperCase()}
          </div>
          <div style={{ marginTop: 4 }}>RACK {r.location || "—"}</div>
        </div>
      </div>

      <div style={{ marginTop: "0.12in", flex: 1 }}>
        <div style={{ marginBottom: "0.1in" }}>
          <div className="lbl">Slab Color</div>
          <div className="val">{r.slab_color}</div>
        </div>

        <div style={{ display: "flex", gap: "0.24in", marginBottom: "0.1in" }}>
          <div>
            <div className="lbl">Usable</div>
            <div className="val font-mono-data" style={{ fontSize: "14pt" }}>
              {r.usable_sqft} ft²
            </div>
          </div>
          <div>
            <div className="lbl">Full Slab</div>
            <div className="val font-mono-data">{fullSqft} ft²</div>
          </div>
          <div>
            <div className="lbl">Thk</div>
            <div className="val font-mono-data">{r.thickness}</div>
          </div>
        </div>

        {poly.length > 2 && (
          <div style={{ marginBottom: "0.06in" }}>
            <div className="lbl">Shape — all sides in inches</div>
            <div style={{ position: "relative", marginTop: "2px" }}>
              <svg
                viewBox={`0 0 ${SW} ${SH}`}
                style={{ width: "100%" }}
                data-testid="label-shape"
              >
                <polygon
                  points={shapePts}
                  fill="#000"
                  fillOpacity="0.1"
                  stroke="#000"
                  strokeWidth="2.5"
                  strokeLinejoin="round"
                />
              </svg>
              {sides.map((sd, i) => (
                <span
                  key={i}
                  data-testid={`side-dim-${i}`}
                  style={{
                    position: "absolute",
                    left: `${(sd.mx / SW) * 100}%`,
                    top: `${(sd.my / SH) * 100}%`,
                    transform: "translate(-50%, -50%)",
                    background: "#fff",
                    padding: "0 2px",
                    fontSize: "8pt",
                    fontWeight: 700,
                    lineHeight: 1,
                  }}
                >
                  {sd.len}"
                </span>
              ))}
            </div>
          </div>
        )}

        {r.source_tag && (
          <div style={{ fontSize: "9pt" }}>
            <span className="lbl">From </span>
            <span style={{ fontWeight: 600 }}>
              {r.source_tag} · {r.customer_name}
            </span>
          </div>
        )}

        <div
          style={{
            borderTop: "1px solid #000",
            marginTop: "0.06in",
            paddingTop: "0.05in",
          }}
        >
          <div className="lbl">Notes</div>
          <div
            style={{
              fontSize: "9.5pt",
              lineHeight: 1.3,
              whiteSpace: "pre-wrap",
            }}
          >
            {r.notes || "—"}
          </div>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          borderTop: "2px solid #000",
          paddingTop: "0.08in",
        }}
      >
        <div style={{ fontSize: "6pt", color: "#333", maxWidth: "2in" }}>
          Scan to open remnant
          <div className="font-mono-data" style={{ fontSize: "5.5pt" }}>
            {r.id}
          </div>
        </div>
        <QRCodeSVG
          value={url}
          size={58}
          level="M"
          bgColor="#ffffff"
          fgColor="#000000"
        />
      </div>
    </div>
  );
};
